import { SPACING } from '@alto/design-library-tokens';
import {
  AltoSpinningLoader,
  Body,
  InlineAlert,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  Row,
  Separator,
} from '@alto/design-system';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { updateOrder } from '~shared/actions/cart';
import { getWindows } from '~shared/features/checkout/selectors/getCart';
import {
  getOrderDate,
  getOrderDeliverAfter,
  getOrderDeliverBefore,
} from '~shared/features/checkout/selectors/getOrder';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useCheckoutAddressInfoKeys } from '../../hooks/useCheckoutAddressInfoKeys';
import { getSplitTimeRangeStrings, getTimeRangeString, getUniqueDeliveryWindows } from './helpers';

export const WindowSelector = () => {
  const dispatch = useDispatchShared();
  const windows = useSelectorShared(getWindows);
  const order_deliver_after = useSelectorShared(getOrderDeliverAfter);
  const order_date = useSelectorShared(getOrderDate);
  const order_deliver_before = useSelectorShared(getOrderDeliverBefore);
  const isEditOrder = useSelectorShared(getIsEditingExistingOrder);
  const fetchDeliveryWindowsLoading = useSelectorShared((state) => state.ui.loading.fetchDeliveryWindowsLoading);
  const { deliveryWindowRequired } = useCheckoutAddressInfoKeys();
  const [value, setValue] = useState<string | null>(null);
  const currentOrderDate = useRef(order_date);

  const uniqueWindowsArray = useMemo(() => getUniqueDeliveryWindows(windows), [windows]);
  const firstWindow = windows?.[0];
  const firstWindowDeliverAfter = firstWindow?.deliver_after;
  const firstWindowDeliverBefore = firstWindow?.deliver_before;

  useEffect(() => {
    if (!firstWindowDeliverAfter || !firstWindowDeliverBefore) return;

    // update the order's window to the first delivery window of the day during checkout
    // OR when editing a scheduled order and the order's date is not the existing
    // shipment's date (because we don't want to override the existing window selected)
    // eslint-disable-next-line sonarjs/no-collapsible-if
    if (!fetchDeliveryWindowsLoading) {
      if (!isEditOrder || (isEditOrder && currentOrderDate.current !== order_date)) {
        dispatch(
          updateOrder({
            deliver_before: firstWindowDeliverBefore,
            deliver_after: firstWindowDeliverAfter,
          }),
        );
      }
    }
  }, [
    dispatch,
    fetchDeliveryWindowsLoading,
    isEditOrder,
    order_date,
    firstWindowDeliverAfter,
    firstWindowDeliverBefore,
  ]);

  const handleWindowPress = useCallback(
    (key: string) => {
      setValue(key);
      const [deliverAfter, deliverBefore] = getSplitTimeRangeStrings(key);
      if (order_deliver_before !== deliverBefore || order_deliver_after !== deliverAfter) {
        dispatch(
          updateOrder({
            deliver_after: deliverAfter,
            deliver_before: deliverBefore,
          }),
        );
      }
    },
    [dispatch, order_deliver_after, order_deliver_before],
  );

  if (fetchDeliveryWindowsLoading) {
    return (
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Row
          flexGrow={1}
          center
        >
          <AltoSpinningLoader />
        </Row>
      </LgPadding>
    );
  }

  if (deliveryWindowRequired && windows.length === 0) {
    return (
      <LgPadding>
        <InlineAlert type="error">
          <Body>No windows available. Please select another date.</Body>
        </InlineAlert>
      </LgPadding>
    );
  }

  return (
    <>
      <InputRadioGroup
        onValueChange={handleWindowPress}
        defaultValue={getTimeRangeString({ deliver_after: order_deliver_after, deliver_before: order_deliver_before })}
      >
        {uniqueWindowsArray.map(({ deliver_after, deliver_before }, index) => {
          const timeRangeString = getTimeRangeString({ deliver_after, deliver_before });
          return (
            <React.Fragment key={timeRangeString}>
              <Separator transparent={!(value === timeRangeString && index === 0)} />
              <InputRadio
                radioPlacement="left"
                key={timeRangeString}
                label={timeRangeString}
                value={timeRangeString}
                LeftContent={<LgSpacing />}
              />
              <Separator transparent={index === windows.length - 1 && value !== timeRangeString} />
            </React.Fragment>
          );
        })}
      </InputRadioGroup>
    </>
  );
};
